import * as React from 'react';
import { DocAccessType, ICourse, PassportDocs, PassportList } from '../../../../types';
import { Section } from '../../../Finger/Section';
import FingerWithApprove from '../../../atoms/finger-with-approve';
import { isPassportExpired, isPassportExpiringSoon } from '../../../../utilities/validation';
import { SummaryRow } from '../../../Summary/SummaryRow';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { getFingerStatus, getVisible, hasDeleted } from '../../../../utilities/documents';
import { UserRole } from '../../../../store/auth/types';
import { parseDate, formatDate } from 'shared-components';

interface IProps {
  infoOnly: boolean;
  userAccess?: DocAccessType;
  documents: PassportDocs;
  passportStatus: string;
  passportExpiryDate: string;
  passportExpectedDate: string;
  warning?: string;
  courses: Array<ICourse>;
  userRole?: UserRole;
  canResume: boolean;
  canUpload: boolean;
  canOpen: boolean;
  canApprove: boolean;
  canReject: boolean;
  isDeleting: boolean;
  canAcknowledge: boolean;
  onResume: (url: string) => void;
  onUploadClick: (sponsorType: string, key: string) => void;
  onOpenClick: (sponsorType: string, key: string) => void;
  onApproveClick: (sponsorType: string, key: string) => void;
  onRejectClick: (sponsorType: string, key: string) => void;
  onRescindRejectClick: (sponsorType: string, key: string) => void;
  onDeleteClick: (sponsorType: string, key: string) => void;
  onAcknowledgeClick: (sponsorType: string, key: string) => void;
}

const Passport = (props: IProps) => {
  const passports = props.documents;
  const { passportStatus, passportExpectedDate, passportExpiryDate, courses, infoOnly, userAccess, userRole } = props;
  let { warning } = props;
  let hasDocs = false;
  let hasDeletedDocs = false;
  const docNodes: React.ReactNode[] = [];

  if (passports.current) {
    const visibleDocs = getVisible({
      documentsHash: passports.current,
      userAccess,
      infoOnly,
      userRole,
    }) as PassportList[];
    if (hasDeleted(passports.current)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: PassportList, index: number) => {
          const { key, item } = doc;
          const passportLabel = `Current passport ${index > 0 ? index + 1 : ''} ${
            item.dateOfExpiry ? `- expiry ${formatDate(parseDate(item.dateOfExpiry), 'DD/MM/YYYY')}` : ''
          }`;
          return (
            <FingerWithApprove
              id={`passport-current-${index}`}
              key={`passport-current-${index}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              label={passportLabel}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              onUploadClick={() => props.onUploadClick('current', key)}
              onOpenClick={() => props.onOpenClick('current', key)}
              onApproveClick={() => props.onApproveClick('current', key)}
              onRejectClick={() => props.onRejectClick('current', key)}
              onRescindRejectClick={() => props.onRescindRejectClick('current', key)}
              onDeleteClick={() => props.onDeleteClick('current', key)}
              onAcknowledgeClick={() => props.onAcknowledgeClick('current', key)}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              <>
                <SummaryRow
                  key={`passport-doc-number-${key}`}
                  label="Passport or Travel Document Number"
                  fullLabel="Passport or Travel Document Number"
                  value={item.docNumber}
                />

                <SummaryRow
                  key={`passport-place-of-issue-${key}`}
                  label="Place of Issue"
                  fullLabel="Place of Issue"
                  value={item.placeOfIssue}
                />

                <SummaryRow
                  key={`passport-date-of-issue-${key}`}
                  label="Date of Issue"
                  fullLabel="Date of Issue"
                  value={item.dateOfIssue ? formatDate(parseDate(item.dateOfIssue), 'DD/MM/YYYY') : ''}
                />

                <SummaryRow
                  key={`passport-date-of-expiry-${key}`}
                  label="Date of Expiry"
                  fullLabel="Date of Expiry"
                  value={item.dateOfExpiry ? formatDate(parseDate(item.dateOfExpiry), 'DD/MM/YYYY') : ''}
                />
              </>
            </FingerWithApprove>
          );
        }),
      );
    }
  }

  if (passports.renew) {
    const visibleDocs = getVisible({
      documentsHash: passports.renew,
      userAccess,
      infoOnly,
      userRole,
    }) as PassportList[];
    if (hasDeleted(passports.renew)) {
      hasDeletedDocs = true;
    }
    if (visibleDocs.length > 0) {
      hasDocs = true;
      docNodes.push(
        ...visibleDocs.map((doc: PassportList, index: number) => {
          const { key, item } = doc;
          const passportLabel = `New/renewed passport ${index > 0 ? index + 1 : ''}`;
          return (
            <FingerWithApprove
              id={`passport-renew-${index}`}
              key={`passport-renew-${index}`}
              status={getFingerStatus(item)}
              isProcessingPdf={item.isProcessingPdf}
              reason={item.reason}
              label={passportLabel}
              fileCount={item.signedUrls && item.signedUrls.length > 0 ? item.signedUrls.length : 0}
              fileNames={item.fileNames}
              canUpload={infoOnly ? false : props.canUpload}
              canOpen={infoOnly ? false : props.canOpen}
              canApprove={infoOnly ? false : props.canApprove}
              canReject={infoOnly ? false : props.canReject}
              isDeleting={infoOnly ? false : props.isDeleting}
              canAcknowledge={infoOnly ? props.canAcknowledge : false}
              onUploadClick={() => props.onUploadClick('renew', key)}
              onOpenClick={() => props.onOpenClick('renew', key)}
              onApproveClick={() => props.onApproveClick('renew', key)}
              onRejectClick={() => props.onRejectClick('renew', key)}
              onRescindRejectClick={() => props.onRescindRejectClick('renew', key)}
              onDeleteClick={() => props.onDeleteClick('renew', key)}
              onAcknowledgeClick={() => props.onAcknowledgeClick('renew', key)}
              userRole={props.userRole}
              deletedByTTL={item.deletedByTTL}
            >
              <>
                <SummaryRow
                  key={`passport-doc-number-${key}`}
                  label="Passport or Travel Document Number"
                  fullLabel="Passport or Travel Document Number"
                  value={item.docNumber}
                />

                <SummaryRow
                  key={`passport-place-of-issue-${key}`}
                  label="Place of Issue"
                  fullLabel="Place of Issue"
                  value={item.placeOfIssue}
                />

                <SummaryRow
                  key={`passport-date-of-issue-${key}`}
                  label="Date of Issue"
                  fullLabel="Date of Issue"
                  value={item.dateOfIssue ? formatDate(parseDate(item.dateOfIssue), 'DD/MM/YYYY') : ''}
                />

                <SummaryRow
                  key={`passport-date-of-expiry-${key}`}
                  label="Date of Expiry"
                  fullLabel="Date of Expiry"
                  value={item.dateOfExpiry ? formatDate(parseDate(item.dateOfExpiry), 'DD/MM/YYYY') : ''}
                />
              </>
            </FingerWithApprove>
          );
        }),
      );
    }
  }

  const passportInvalidForCourse = passportStatus === 'Yes' && isPassportExpiringSoon(courses, passportExpiryDate);

  if (!warning) {
    if (passportStatus === 'Yes' && isPassportExpired(passportExpiryDate)) {
      warning = 'Passport has expired and must be renewed before uploading again';
    } else if (passportInvalidForCourse) {
      warning = 'Passport expires soon, we recommend it is renewed as soon as possible';
    } else if (passportStatus !== 'Yes') {
      warning = `Expected date ${passportExpectedDate}`;
    }
  }

  if (!hasDocs && !hasDeletedDocs) {
    return null;
  }

  if ((infoOnly || userAccess) && !hasDocs) {
    return null;
  }

  return (
    <Section
      id="passport"
      label={`Please upload a scan of your passport ID page`}
      warning={warning}
      canEdit={props.canResume}
      edit={() => {
        props.onResume('personal-info/step-7');
      }}
    >
      {hasDocs ? docNodes : <NoDocuments />}
    </Section>
  );
};

export default Passport;
